import { useState } from 'react';
import { MetricCard } from '../components/MetricCard';
import { TimeSeriesChart, type TimeSeriesData } from '../components/TimeSeriesChart';
import { RangeSelector } from '../components/RangeSelector';
import { useMockData } from '../hooks/useMockData';

export default function Home() {
  const { data, loading } = useMockData();
  const [range, setRange] = useState<'7d' | '30d' | '90d'>('30d');
  
  if (loading || !data || !data.daily_metrics.length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }
  
  const latest = data.daily_metrics[0]!;
  const yesterday = data.daily_metrics[1]!;
  
  // Calculate 24h change
  const txChange = ((latest.tx_count - yesterday.tx_count) / yesterday.tx_count) * 100;
  const gasChange = ((latest.avg_gas_gwei - yesterday.avg_gas_gwei) / yesterday.avg_gas_gwei) * 100;
  const dauChange = ((latest.daily_active_users - yesterday.daily_active_users) / yesterday.daily_active_users) * 100;
  const blockTimeChange = ((latest.avg_block_time - yesterday.avg_block_time) / yesterday.avg_block_time) * 100;
  
  // Format gas price (convert to mGwei if < 0.01)
  const gasDisplay = latest.avg_gas_gwei < 0.01 
    ? `${(latest.avg_gas_gwei * 1000).toFixed(2)} mGwei`
    : `${latest.avg_gas_gwei.toFixed(4)} Gwei`;
  
  // Filter data by range
  const rangeDays = { '7d': 7, '30d': 30, '90d': 90 }[range];
  const filteredData = data.daily_metrics.slice(0, rangeDays);
  
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Overview</h1>
        <p className="text-gray-600 dark:text-gray-400">Real-time blockchain metrics and insights</p>
      </div>
      
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard 
          title="24h Transactions" 
          value={latest.tx_count.toLocaleString()} 
          change24h={txChange}
        />
        <MetricCard 
          title="Avg Gas Price" 
          value={gasDisplay}
          change24h={gasChange}
        />
        <MetricCard 
          title="Daily Active Users" 
          value={latest.daily_active_users.toLocaleString()}
          change24h={dauChange}
        />
        <MetricCard 
          title="Avg Block Time" 
          value={`${latest.avg_block_time}s`}
          change24h={blockTimeChange}
        />
      </div>
      
      {/* Range Selector */}
      <div className="flex justify-end">
        <RangeSelector value={range} onChange={setRange} />
      </div>
      
      {/* Transaction Chart */}
      <TimeSeriesChart
        title={`Daily Transactions (${rangeDays} Days)`}
        data={filteredData as unknown as TimeSeriesData[]}
        series={[
          { 
            key: 'tx_count', 
            name: 'Transactions', 
            color: '#3b82f6',
            formatter: (value) => value !== undefined ? value.toLocaleString() : 'N/A'
          }
        ]}
      />
      
      {/* Active Users Chart */}
      <TimeSeriesChart
        title={`Daily Active Users (${rangeDays} Days)`}
        data={filteredData as unknown as TimeSeriesData[]}
        series={[
          { 
            key: 'daily_active_users', 
            name: 'Active Users', 
            color: '#10b981',
            formatter: (value) => value !== undefined ? value.toLocaleString() : 'N/A'
          }
        ]}
      />
    </div>
  );
}
